import { Link } from "@tanstack/react-router";
import {
  Divider,
  Drawer,
} from "@totalenergiescode/mobility-business-rev-design-system";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetUserDetail } from "@/api/graphql/accounts/getUser/hooks.ts";
import type { UserDetailBO } from "@/api/graphql/accounts/getUser/types.ts";
import type { AccountsMsSearchUsersResponseData } from "@/api/graphql/types/graphql.ts";
import { AccountsList } from "@/components/Accounts/list/component";
import { UserActionsList } from "@/components/actions/users/component.tsx";
import type { BreadcrumbItem } from "@/components/shared/Breadcrumb/types.ts";
import { Loading } from "@/components/shared/Loading/component";
import { useBreadCrumbItems } from "@/components/Users/details/hooks.ts";
import { useAbilities } from "@/hooks/ability/hooks";

import { StartSupportSessionDrawer } from "./startSessionDrawer/component";
import {
  StyledArrowRight,
  StyledButtonHeaderContainerDiv,
  StyledButtonNotifications,
  StyledCardContainerDiv,
  StyledCardContent,
  StyledCardDiv,
  StyledCardTitle,
  StyledContainerDiv,
  StyledIdentity,
  StyledPageHeader,
  StyledRoundedTopRightCardDiv,
  StyledSectionContainerDiv,
  StyledTitle,
} from "./styles";

interface UserDetailProps {
  id: string;
}

const getUserName = (user: UserDetailBO) =>
  user?.firstName + " " + user.lastName || "N F";

export function UserDetail({ id }: UserDetailProps) {
  const { t: tUsers } = useTranslation("users");
  const { t: tNotifications } = useTranslation("notifications");
  const { userData, isLoading } = useGetUserDetail(id);
  const { can, isLoading: isMeLoading } = useAbilities();
  const breadcrumbItems: BreadcrumbItem[] = useBreadCrumbItems(
    userData as AccountsMsSearchUsersResponseData,
  );
  const [showStartSessionAgent, setShowStartSessionAgent] = useState(false);
  const isSuperAgent = can("SUPER_AGENT");
  const hasImpersonateRight = can("IMPERSONATE");

  if (!userData) return <div>User not found</div>;

  const {
    id: userDataId,
    firstName,
    lastName,
    email,
    phone,
    jobTitle,
  } = userData;
  const userName = getUserName(userData);

  return (
    <StyledContainerDiv>
      <Loading isLoading={isLoading}>
        <>
          <StyledPageHeader
            breadcrumbItems={breadcrumbItems}
            title={userName}
            href={""}
            rightContent={
              <StyledButtonHeaderContainerDiv>
                {hasImpersonateRight && (
                  <StyledButtonNotifications
                    onClick={() => {
                      setShowStartSessionAgent(true);
                    }}
                    buttonText={tUsers("users.impersonate.button")}
                    variant={"primary"}
                    isLowercase
                    size={"m"}
                  />
                )}
                <Link
                  to='/users/$id/notifications'
                  params={{ id: userDataId || "" }}
                >
                  <StyledButtonNotifications
                    onClick={() => {}}
                    buttonText={tNotifications("notifications.button.name")}
                    variant={"primary"}
                    isLowercase
                    leftIcon={<StyledArrowRight />}
                    size={"m"}
                  />
                </Link>
              </StyledButtonHeaderContainerDiv>
            }
          />
          <StyledSectionContainerDiv>
            <StyledTitle variant='title2'>
              {tUsers("users.list.subtitle.info")}
            </StyledTitle>
            <StyledCardContainerDiv>
              <StyledCardDiv>
                <StyledCardTitle variant='title2'>
                  {tUsers("users.cards.user")}
                </StyledCardTitle>
                <Divider />
                <StyledIdentity
                  avatar={{
                    firstName,
                    lastName,
                    type: "initials",
                  }}
                  subtitle={email}
                  title={userName}
                />
              </StyledCardDiv>
              <StyledCardDiv>
                <StyledCardTitle variant='title2'>
                  {" "}
                  {tUsers("users.cards.phoneNumber")}
                </StyledCardTitle>
                <Divider />
                <StyledCardContent variant='title2'>{phone}</StyledCardContent>
              </StyledCardDiv>
              <StyledRoundedTopRightCardDiv>
                <StyledCardTitle variant='title2'>
                  {tUsers("users.cards.jobTitle")}
                </StyledCardTitle>
                <Divider />
                <StyledCardContent variant='title2'>
                  {jobTitle}
                </StyledCardContent>
              </StyledRoundedTopRightCardDiv>
            </StyledCardContainerDiv>
          </StyledSectionContainerDiv>
          <AccountsList userId={id} />
          <Loading isLoading={isMeLoading}>
            <>{isSuperAgent && <UserActionsList userId={id} />}</>
          </Loading>
          <Drawer
            isOpen={showStartSessionAgent}
            onClose={() => setShowStartSessionAgent(false)}
          >
            <StartSupportSessionDrawer
              user={userData}
              onClose={() => setShowStartSessionAgent(false)}
            />
          </Drawer>
        </>
      </Loading>
    </StyledContainerDiv>
  );
}
